import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Tooltip,
  Typography
} from '@mui/material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FacebookIcon from '@mui/icons-material/Facebook'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InstagramIcon from '@mui/icons-material/Instagram'
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PublicIcon from '@mui/icons-material/Public'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import TelegramIcon from '@mui/icons-material/Telegram'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

const getGroupKey = group => group.registrationnumberrequestid || 'unidentified'

const getProviderVisual = chat => {
  const providerId = Number(chat.messageproviderid)
  const providerName = String(chat.messageprovidername || '').toLowerCase()

  if (providerId === 2 || providerName.includes('whatsapp')) return { Icon: WhatsAppIcon, background: '#25D366' }
  if (providerId === 3 || providerName.includes('instagram')) {
    return {
      Icon: InstagramIcon,
      background: 'radial-gradient(circle at 30% 107%, #FDF497 0%, #FD5949 42%, #D6249F 62%, #285AEB 100%)'
    }
  }
  if (providerId === 4 || providerName.includes('messenger') || providerName.includes('facebook')) {
    return { Icon: FacebookIcon, background: '#1877F2' }
  }
  if (providerId === 5 || providerName.includes('telegram')) return { Icon: TelegramIcon, background: '#229ED9' }
  if (providerId === 6 || providerName.includes('webchat')) return { Icon: PublicIcon, background: '#6750A4' }
  return { Icon: ChatBubbleOutlineIcon, background: '#607D8B' }
}

const SummaryField = ({ Icon, label, value, divider }) => (
  <Box
    display="flex"
    alignItems="center"
    sx={{
      minWidth: 0,
      gap: '10px',
      paddingLeft: { xs: 0, lg: divider ? '18px' : 0 },
      borderLeft: { xs: 0, lg: divider ? '1px solid #E0E5EA' : 0 }
    }}
  >
    <Icon aria-hidden="true" sx={{ flex: '0 0 auto', color: '#73BE35', fontSize: 19 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '11px', lineHeight: 1.15 }}>
        {label}
      </Typography>
      <Typography variant="body2" noWrap title={value} sx={{ color: '#183652', fontSize: '13px', fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  </Box>
)

SummaryField.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  divider: PropTypes.bool
}

const TeamMismatchConversations = ({
  groups,
  canRepairTeams,
  loading,
  repairing,
  intl,
  onRepairChat,
  onRepairAll,
  embedded,
  emptyMessage
}) => {
  const [expandedGroupKey, setExpandedGroupKey] = useState(null)
  const totalChats = (groups || []).reduce((total, group) => total + (group.chats || []).length, 0)

  return (
    <Box
      sx={{
        width: '100%',
        marginTop: embedded ? 0 : '20px',
        marginBottom: embedded ? '20px' : '160px',
        padding: { xs: '10px', sm: '14px' },
        backgroundColor: '#F8FAFC',
        borderRadius: '10px',
        boxSizing: 'border-box'
      }}
    >
      {!embedded && (
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ marginBottom: '14px' }}>
          <Box>
            <Typography variant="h5" color="primary">
              {intl.formatMessage({ id: 'chat.team.mismatch.section.title' })}
            </Typography>
            {!loading && totalChats > 0 && (
              <Typography variant="body2" color="textSecondary">
                {intl.formatMessage({ id: 'chat.team.mismatch.count' }, { count: totalChats })}
              </Typography>
            )}
          </Box>
          {loading && <CircularProgress size={24} />}
        </Box>
      )}

      {embedded && loading && (
        <Box display="flex" justifyContent="flex-end" sx={{ marginBottom: '10px' }}>
          <CircularProgress size={22} />
        </Box>
      )}

      <Box
        display="flex"
        alignItems="flex-start"
        sx={{
          gap: '9px',
          marginBottom: '12px',
          padding: '10px 12px',
          color: '#31546F',
          backgroundColor: '#EEF6FC',
          border: '1px solid #D8E8F4',
          borderRadius: '8px'
        }}
      >
        <InfoOutlinedIcon aria-hidden="true" sx={{ flex: '0 0 auto', color: '#4D86AE', fontSize: 18, marginTop: '1px' }} />
        <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.45 }}>
          {intl.formatMessage({ id: 'chat.team.mismatch.description' })}
        </Typography>
      </Box>

      {!loading && (!groups || groups.length === 0) && (
        <Typography variant="body2" color="textSecondary" sx={{ padding: '8px 2px' }}>
          {emptyMessage || intl.formatMessage({ id: 'chat.team.mismatch.empty' })}
        </Typography>
      )}

      <Box display="grid" sx={{ gap: '12px' }}>
        {(groups || []).map(group => {
          const groupKey = getGroupKey(group)
          const chats = group.chats || []
          const providerNames = [...new Set(chats.map(chat => chat.messageprovidername).filter(Boolean))]
          const canRepairGroup = canRepairTeams && Boolean(group.registrationnumberrequestid)
          const registrationId = group.registrationnumberrequestid ? `#${group.registrationnumberrequestid}` : '-'
          const phoneNumber = group.registrationnumberrequestphonenumber
            || intl.formatMessage({ id: 'chat.without.channel.unknown.registration' })
          const account = group.registrationnumberrequestaccountid || '-'
          const registrationCreatedAt = group.registrationnumberrequestcreatedat
            ? intl.formatDate(new Date(group.registrationnumberrequestcreatedat), {
              year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
            })
            : '-'

          return (
            <Accordion
              key={groupKey}
              disableGutters
              expanded={expandedGroupKey === groupKey}
              onChange={(event, expanded) => setExpandedGroupKey(expanded ? groupKey : null)}
              sx={{
                border: '1px solid #E0E5EA',
                borderRadius: '10px !important',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 }
              }}
            >
              <AccordionSummary
                id={`team-mismatch-summary-${groupKey}`}
                aria-controls={`team-mismatch-panel-${groupKey}`}
                expandIcon={<ExpandMoreIcon sx={{ color: '#73BE35' }} />}
                sx={{
                  minHeight: { xs: 'auto', lg: '72px' },
                  padding: { xs: '12px', sm: '12px 18px' },
                  '&.Mui-expanded': { minHeight: { xs: 'auto', lg: '72px' } },
                  '& .MuiAccordionSummary-content': { margin: '0 !important', minWidth: 0 }
                }}
              >
                <Box
                  display="flex"
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: { xs: 'stretch', lg: 'center' },
                    gap: { xs: '12px', lg: '18px' }
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: '120px minmax(180px, 1.2fr) minmax(170px, 1fr) minmax(190px, 1fr)'
                      },
                      gap: { xs: '10px', sm: '14px', lg: 0 }
                    }}
                  >
                    <SummaryField Icon={NumbersOutlinedIcon} label={intl.formatMessage({ id: 'chat.without.channel.registration.id' })} value={registrationId} />
                    <SummaryField Icon={PhoneOutlinedIcon} label={intl.formatMessage({ id: 'chat.without.channel.registered.number' })} value={phoneNumber} divider />
                    <SummaryField Icon={AccountCircleOutlinedIcon} label={intl.formatMessage({ id: 'chat.without.channel.account' })} value={account} divider />
                    <SummaryField Icon={CalendarMonthIcon} label={intl.formatMessage({ id: 'chat.without.channel.registration.created.at' })} value={registrationCreatedAt} divider />
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ gap: '8px', flexShrink: 0 }}>
                    {providerNames.map(providerName => {
                      const providerVisual = getProviderVisual({ messageprovidername: providerName })
                      const ProviderIcon = providerVisual.Icon
                      return (
                        <Tooltip key={providerName} title={`${intl.formatMessage({ id: 'chat.without.channel.provider' })}: ${providerName}`} arrow>
                          <Box
                            component="span"
                            role="img"
                            aria-label={`${intl.formatMessage({ id: 'chat.without.channel.provider' })}: ${providerName}`}
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: 34, height: 34, color: '#FFFFFF', background: providerVisual.background, borderRadius: '50%' }}
                          >
                            <ProviderIcon htmlColor="#FFFFFF" sx={{ color: '#FFFFFF !important', fill: '#FFFFFF', fontSize: 20 }} />
                          </Box>
                        </Tooltip>
                      )
                    })}
                    <Chip
                      size="small"
                      label={chats.length}
                      aria-label={intl.formatMessage({ id: 'chat.without.channel.group.count' }, { count: chats.length })}
                      sx={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: '#F2F6FA', fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails
                id={`team-mismatch-panel-${groupKey}`}
                aria-labelledby={`team-mismatch-summary-${groupKey}`}
                sx={{ padding: { xs: '12px', sm: '14px 16px 16px' }, borderTop: '1px solid #E5EAF0' }}
              >
                {canRepairGroup && (
                  <Box display="flex" justifyContent="flex-end" sx={{ marginBottom: '12px' }}>
                    <Button color="primary" variant="contained" size="small" disabled={repairing} onClick={() => onRepairAll(group)}>
                      {intl.formatMessage({ id: 'chat.team.mismatch.repair.all' })}
                    </Button>
                  </Box>
                )}

                <Grid container spacing={1.5} alignItems="stretch">
                  {chats.map(chat => {
                    const customerName = chat.customername
                      || intl.formatMessage({ id: 'chat.without.channel.customer.not.identified' })
                    const customerNumber = chat.customerphone || chat.senderid || '-'
                    const technicianName = chat.assignedusername
                      || (chat.assigneduserid ? `#${chat.assigneduserid}` : intl.formatMessage({ id: 'chat.without.channel.technician.unassigned' }))
                    const createdAt = chat.createdat
                      ? intl.formatDate(new Date(chat.createdat), {
                        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                      })
                      : '-'
                    const currentTeam = chat.currenttechinicalteamname || `#${chat.currenttechinicalteamid || '-'}`
                    const targetTeam = chat.targettechinicalteamname || `#${chat.targettechinicalteamid || '-'}`
                    const providerVisual = getProviderVisual(chat)
                    const ProviderIcon = providerVisual.Icon

                    return (
                      <Grid item xs={12} sm={6} lg={4} xl={3} key={chat.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: '100%',
                            padding: '14px',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#FBFCFE',
                            borderColor: '#D9E1E8',
                            borderRadius: '12px',
                            boxShadow: '0 4px 14px rgba(39, 54, 74, 0.08)'
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: '10px' }}>
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, gap: '9px' }}>
                              <Box
                                role="img"
                                aria-label={chat.messageprovidername || intl.formatMessage({ id: 'chat.without.channel.provider' })}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{ width: 34, height: 34, flex: '0 0 34px', color: '#FFFFFF', background: providerVisual.background, borderRadius: '9px' }}
                              >
                                <ProviderIcon htmlColor="#FFFFFF" sx={{ color: '#FFFFFF !important', fill: '#FFFFFF', fontSize: 19 }} />
                              </Box>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`ID #${chat.id}`}
                                sx={{ height: 30, color: '#174A7A', backgroundColor: '#F1F7FD', borderColor: '#B8D3EC', borderRadius: '7px', fontSize: '12px', fontWeight: 700 }}
                              />
                            </Box>
                            {chat.unreadmessages > 0 && (
                              <Chip
                                size="small"
                                label={intl.formatMessage({ id: 'chat.without.channel.unread.messages' }, { count: chat.unreadmessages })}
                                sx={{ height: 26, flexShrink: 0, backgroundColor: '#FFFFFF', border: '1px solid #D9E2EA', fontSize: '11px' }}
                              />
                            )}
                          </Box>

                          <Box display="grid" sx={{ gap: '9px', marginTop: '14px' }}>
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, gap: '8px' }}>
                              <PersonOutlineIcon aria-hidden="true" sx={{ color: '#365775', fontSize: 17 }} />
                              <Typography variant="body2" noWrap title={customerName} sx={{ color: '#183652', fontSize: '12px', fontWeight: 600 }}>
                                {customerName}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, gap: '8px' }}>
                              <PhoneOutlinedIcon aria-hidden="true" sx={{ color: '#7890A7', fontSize: 16 }} />
                              <Typography variant="body2" noWrap title={customerNumber} sx={{ color: '#41617E', fontSize: '11px' }}>
                                {customerNumber}
                              </Typography>
                            </Box>
                          </Box>

                          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: '10px', marginTop: '12px' }}>
                            <Chip size="small" variant="outlined" label={chat.chatstatusname || '-'} sx={{ height: 24, color: '#174CC5', borderColor: '#3F6FE5', backgroundColor: '#FFFFFF', fontSize: '11px' }} />
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, gap: '5px', color: '#64748B' }}>
                              <ScheduleIcon aria-hidden="true" sx={{ fontSize: 15 }} />
                              <Typography variant="caption" noWrap title={createdAt} sx={{ fontSize: '10px' }}>
                                {intl.formatMessage({ id: 'chat.without.channel.created.at' })} {createdAt}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ marginTop: '12px', padding: '9px 11px', backgroundColor: '#F3F6F9', border: '1px solid #DEE6ED', borderRadius: '8px' }}>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '10px' }}>
                              {intl.formatMessage({ id: 'chat.team.mismatch.channel' })}
                            </Typography>
                            <Typography variant="body2" noWrap title={chat.channelname || '-'} sx={{ color: '#203C59', fontSize: '11px', fontWeight: 700 }}>
                              {chat.channelname || '-'}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" sx={{ gap: '8px', marginTop: '8px', padding: '9px 11px', backgroundColor: '#F2F8EE', border: '1px solid #D7E9CB', borderRadius: '8px' }}>
                            <GroupsOutlinedIcon aria-hidden="true" sx={{ color: '#5BA32B', fontSize: 17 }} />
                            <Typography variant="caption" noWrap title={currentTeam} sx={{ minWidth: 0, color: '#6B7280', fontSize: '10px' }}>
                              {currentTeam}
                            </Typography>
                            <ArrowForwardIcon aria-hidden="true" sx={{ flex: '0 0 auto', color: '#73BE35', fontSize: 16 }} />
                            <Typography variant="caption" noWrap title={targetTeam} sx={{ minWidth: 0, color: '#244A16', fontSize: '10px', fontWeight: 700 }}>
                              {targetTeam}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: '10px', marginTop: '8px', padding: '9px 11px', backgroundColor: '#F3F6F9', border: '1px solid #DEE6ED', borderRadius: '8px' }}>
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, gap: '7px', color: '#536B82' }}>
                              <SupportAgentIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                              <Typography variant="caption" noWrap sx={{ fontSize: '10px' }}>
                                {intl.formatMessage({ id: 'chat.without.channel.technician' })}
                              </Typography>
                            </Box>
                            <Typography variant="caption" noWrap title={technicianName} sx={{ color: '#203C59', fontSize: '10px', fontWeight: 700 }}>
                              {technicianName}
                            </Typography>
                          </Box>

                          {canRepairTeams && (
                            <Box sx={{ marginTop: 'auto', paddingTop: '12px' }}>
                              <Divider sx={{ marginBottom: '9px', borderColor: '#DDE9D6' }} />
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={repairing}
                                onClick={() => onRepairChat(chat.id, group)}
                                sx={{ color: '#5AAE20', borderColor: '#70C43A', borderRadius: '7px', fontSize: '11px', fontWeight: 600 }}
                              >
                                {intl.formatMessage({ id: 'chat.team.mismatch.repair.one' })}
                              </Button>
                            </Box>
                          )}
                        </Card>
                      </Grid>
                    )
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    </Box>
  )
}

TeamMismatchConversations.defaultProps = {
  embedded: false,
  emptyMessage: null
}

TeamMismatchConversations.propTypes = {
  groups: PropTypes.array.isRequired,
  canRepairTeams: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  repairing: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  onRepairChat: PropTypes.func.isRequired,
  onRepairAll: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
  emptyMessage: PropTypes.string
}

export default TeamMismatchConversations
